import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface BookReview {
  id: string;
  book_id: string;
  rating_star: number;
  review_text?: string;
  created_at: string;
  user_id: string;
  is_following: boolean;
  like_count: number;
  profiles: {
    id: string;
    name?: string;
    username?: string;
    image?: string;
    is_private_name?: boolean;
  };
  books: {
    id: string;
    title: string;
    authors: string[];
    cover_url?: string;
  };
}

export interface BookReviewStats {
  averageRating: number;
  totalRatings: number;
  ratingDistribution: { [key: number]: number };
}

export function useBookReviews(bookId: string, filterRating: number | null = null, sortBy: 'recent' | 'popular' = 'recent') {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<BookReview[]>([]);
  const [stats, setStats] = useState<BookReviewStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookId) return;
    fetchReviews();
  }, [bookId, user]);

  const fetchReviews = async () => {
    if (!bookId) return;
    
    try {
      setLoading(true);

      // Fetch all public reviews with profile data
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          rating_star,
          review_text,
          created_at,
          user_id,
          books!user_books_book_id_fkey(id, title, authors, cover_url),
          profiles(id, name, username, image, is_private_name)
        `)
        .eq('book_id', bookId)
        .eq('privacy', 'public')
        .not('rating_star', 'is', null)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      // Get like counts for all reviews
      const reviewIds = reviewsData?.map(r => r.id) || [];
      const { data: likesData } = await supabase
        .from('review_likes')
        .select('review_id')
        .in('review_id', reviewIds);

      // Count likes per review
      const likeCounts = likesData?.reduce((acc, like) => {
        acc[like.review_id] = (acc[like.review_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Check if current user follows any of these reviewers
      let followedUserIds: string[] = [];
      if (user) {
        const { data: followsData } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user.id)
          .eq('status', 'accepted');
        
        followedUserIds = followsData?.map(f => f.following_id) || [];
      }

      // Transform data - Supabase returns arrays for joins, so we need to extract first element
      const transformedReviews: BookReview[] = (reviewsData || []).map(review => {
        const profileData = Array.isArray(review.profiles) ? review.profiles[0] : review.profiles;
        const bookData = Array.isArray(review.books) ? review.books[0] : review.books;
        
        return {
          id: review.id,
          book_id: review.book_id,
          rating_star: review.rating_star,
          review_text: review.review_text || undefined,
          created_at: review.created_at,
          user_id: review.user_id,
          is_following: followedUserIds.includes(review.user_id),
          like_count: likeCounts[review.id] || 0,
          profiles: profileData,
          books: bookData
        };
      });

      setReviews(transformedReviews);

      // Calculate stats
      if (transformedReviews.length > 0) {
        const totalRatings = transformedReviews.length;
        const sumRatings = transformedReviews.reduce((sum, r) => sum + r.rating_star, 0);
        const averageRating = sumRatings / totalRatings;

        // Rating distribution
        const distribution: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        transformedReviews.forEach(r => {
          distribution[r.rating_star] = (distribution[r.rating_star] || 0) + 1;
        });

        setStats({
          averageRating,
          totalRatings,
          ratingDistribution: distribution
        });
      } else {
        setStats(null);
      }
    } catch (error) {
      console.error('Error fetching book reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filtering and sorting
  const filteredAndSortedReviews = (() => {
    let filtered = reviews;

    // Apply star rating filter
    if (filterRating !== null) {
      filtered = filtered.filter(r => r.rating_star === filterRating);
    }

    // Sort reviews
    const sorted = [...filtered].sort((a, b) => {
      // Always prioritize followed users
      if (a.is_following && !b.is_following) return -1;
      if (!a.is_following && b.is_following) return 1;

      // Apply secondary sort
      if (sortBy === 'popular') {
        if (b.like_count !== a.like_count) {
          return b.like_count - a.like_count;
        }
      }

      // Finally sort by date
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return sorted;
  })();

  return {
    reviews,
    filteredReviews: filteredAndSortedReviews,
    stats,
    loading,
    refreshReviews: fetchReviews
  };
}
